import { apiFetch } from './api';
import { Product, ProductData, ProductStatus } from '../types/product';

// ─── Products ─────────────────────────────────────────────────────────────────

export const fetchAllProducts = (): Promise<Product[]> =>
  apiFetch<Product[]>('/aurel/products/all_products');

export const fetchProduct = (id: string | number): Promise<Product> =>
  apiFetch<Product>(`/aurel/products/get_product/${id}`);

export const createProduct = (data: ProductData): Promise<{ insertId: number }> =>
  apiFetch<{ insertId: number }>('/aurel/products/add_product', {
    method: 'POST',
    body: JSON.stringify(data),
  });

export const updateProduct = (id: string | number, data: Partial<ProductData>): Promise<unknown> =>
  apiFetch<unknown>(`/aurel/products/update_product/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });

export const updateProductStatus = (id: string | number, status: ProductStatus): Promise<{ message: string }> =>
  apiFetch<{ message: string }>(`/aurel/products/update_status/${id}`, {
    method: 'PATCH',
    body: JSON.stringify({ status }),
  });

export const deleteProduct = (id: string | number): Promise<{ message: string }> =>
  apiFetch<{ message: string }>(`/aurel/products/delete_product/${id}`, {
    method: 'DELETE',
  });

export interface BulkImportResult {
  row: number;
  product_key: string;
  status: 'success' | 'error';
  productId?: number;
  message?: string;
}

export interface BulkImportResponse {
  totalRows: number;
  successCount: number;
  failureCount: number;
  results: BulkImportResult[];
}

export const bulkImportProducts = (file: File): Promise<BulkImportResponse> => {
  const formData = new FormData();
  formData.append('file', file);
  return apiFetch<BulkImportResponse>('/aurel/products/bulk_import', {
    method: 'POST',
    body: formData,
  });
};

export const uploadProductTds = (file: File): Promise<{ fileKey: string; url: string }> => {
  const formData = new FormData();
  formData.append('productTds', file);
  return apiFetch<{ fileKey: string; url: string }>('/aurel/products/upload-tds', {
    method: 'POST',
    body: formData,
  });
};

// ─── SEO Tags ─────────────────────────────────────────────────────────────────

export interface ProductSeoData {
  product_id?: number;
  slug?: string;
  og_title?: string;
  og_description?: string;
  og_image?: string;
  keywords?: string;
  canonical_url?: string;
  robots_meta?: string;
}

export interface ProductSeoRecord extends ProductSeoData {
  id: number;
  createdAt: string;
  updatedAt: string;
}

export const fetchAllSeo = (): Promise<ProductSeoRecord[]> =>
  apiFetch<ProductSeoRecord[]>('/aurel/product-seo/all');

export const fetchSeoByProductId = (productId: string): Promise<ProductSeoRecord | null> =>
  apiFetch<ProductSeoRecord | null>(`/aurel/product-seo/get/${productId}`);

export const createSeo = (data: ProductSeoData): Promise<{ insertId: number }> =>
  apiFetch<{ insertId: number }>('/aurel/product-seo/add', {
    method: 'POST',
    body: JSON.stringify(data),
  });

export const updateSeo = (productId: string, data: Partial<ProductSeoData>): Promise<{ message: string }> =>
  apiFetch<{ message: string }>(`/aurel/product-seo/update/${productId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });

export const deleteSeo = (productId: string): Promise<{ message: string }> =>
  apiFetch<{ message: string }>(`/aurel/product-seo/delete/${productId}`, {
    method: 'DELETE',
  });
